import { useState, useEffect, useMemo } from 'react'
import { Lock, MoreVertical, Trash2, Edit3, Shield, Unlock } from 'lucide-react'
import { Button } from '@quest-lock/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@quest-lock/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@quest-lock/ui/alert-dialog'
import { toast } from 'sonner'
import { AuthenticatorEntry } from '@/authenticator/Types'
import { TOTPGenerator } from '@/authenticator/TOTPGenerator'
import TOTPTimer from './TOTPTimer'
import { useTOTPTimer } from './TOTPTimerContext'

interface AuthenticatorItemProps {
  authenticator: AuthenticatorEntry
  onLock: (id: string) => void
  onDelete: (id: string) => void
  onRename: (id: string, newName: string) => void
  onViewQuests?: (id: string) => void
}

const AuthenticatorItem = ({
  authenticator,
  onLock,
  onDelete,
  onRename,
  onViewQuests
}: AuthenticatorItemProps) => {
  const [totpCode, setTotpCode] = useState<string>('------')
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState(authenticator.name)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const totpGenerator = useMemo(() => new TOTPGenerator(), [])
  const { currentTime } = useTOTPTimer()

  useEffect(() => {
    if (!authenticator.isLocked && authenticator.secret) {
      generateTOTP()
    }
  }, [authenticator, currentTime])

  const generateTOTP = async () => {
    if (!authenticator.secret) return

    try {
      const code = await totpGenerator.generateTOTP(authenticator.secret)
      setTotpCode(code)
    } catch (error) {
      console.error('Failed to generate TOTP:', error)
      setTotpCode('ERROR')
    }
  }

  const handleCopyCode = async () => {
    if (totpCode === '------' || totpCode === 'ERROR') return

    try {
      await navigator.clipboard.writeText(totpCode)
      toast.success('Code copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy code')
    }
  }

  const handleRename = () => {
    if (newName.trim() && newName !== authenticator.name) {
      onRename(authenticator.id, newName.trim())
      toast.success('Authenticator renamed')
    }
    setIsRenaming(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename()
    } else if (e.key === 'Escape') {
      setNewName(authenticator.name)
      setIsRenaming(false)
    }
  }

  const formatTOTPCode = (code: string) => {
    if (code.length !== 6) return code
    return `${code.slice(0, 3)} ${code.slice(3)}`
  }

  const getDisplayName = () => {
    if (authenticator.issuer && authenticator.account) {
      return `${authenticator.issuer} (${authenticator.account})`
    } else if (authenticator.issuer) {
      return authenticator.issuer
    } else if (authenticator.account) {
      return authenticator.account
    }
    return authenticator.name
  }

  const handleDeleteClick = () => {
    setShowDeleteDialog(true)
  }

  const handleConfirmDelete = () => {
    onDelete(authenticator.id)
    setShowDeleteDialog(false)
    toast.success('Authenticator deleted')
  }

  return (
    <div className="group border-shire-bark/20 hover:border-shire-bark/40 rounded-xl border-2 bg-white/90 p-4 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center space-x-3">
            {isRenaming ? (
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onBlur={handleRename}
                onKeyDown={handleKeyPress}
                className="border-shire-sun text-shire-dark border-b-2 bg-transparent font-semibold focus:outline-none"
                autoFocus
              />
            ) : (
              <h3 className="text-shire-dark font-semibold">
                {authenticator.name}
              </h3>
            )}
          </div>

          {(authenticator.issuer || authenticator.account) && (
            <p className="text-shire-bark/70 mb-3 text-sm">
              {getDisplayName()}
            </p>
          )}

          <div className="flex items-center space-x-3">
            {authenticator.isLocked ? (
              <div className="flex flex-col space-y-2">
                {onViewQuests && (
                  <Button
                    size="sm"
                    onClick={() => onViewQuests(authenticator.id)}
                    className="w-full font-mono"
                  >
                    <Shield className="size-5" />
                    Complete quests to unlock
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div
                  className="text-shire-dark hover:text-shire-sun cursor-pointer font-mono text-3xl font-bold transition-colors"
                  onClick={handleCopyCode}
                  title="Click to copy"
                >
                  {formatTOTPCode(totpCode)}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {!authenticator.isLocked && (
            <TOTPTimer onExpired={generateTOTP} size="md" showText={false} />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsRenaming(true)}>
                <Edit3 className="mr-2 size-4" />
                Rename
              </DropdownMenuItem>

              {authenticator.isLocked ? (
                onViewQuests && (
                  <DropdownMenuItem
                    onClick={() => onViewQuests(authenticator.id)}
                  >
                    <Unlock className="mr-2 size-4" />
                    View Quests
                  </DropdownMenuItem>
                )
              ) : (
                <DropdownMenuItem onClick={() => onLock(authenticator.id)}>
                  <Lock className="mr-2 size-4" />
                  Lock with Quests
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={handleDeleteClick}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Authenticator?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{authenticator.name}"? This
              action cannot be undone and you will lose access to the 2FA codes
              for this account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 text-gray-100 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default AuthenticatorItem
